/**
 * ApiKeysPage.tsx — API Keys Management View.
 *
 * Lists keys via GET /v1/api-keys, create key modal via POST /v1/applications,
 * revoke key dialog via DELETE /v1/api-keys/:id, mask/reveal key secret, copy key button,
 * email verification guard banner if user email is unverified, loading skeletons, and toasts.
 */

import { useCallback, useEffect, useState } from 'react';
import { useToast } from '../../components/ui/Toast';
import { useModal } from '../../components/ui/Modal';
import { SkeletonCard } from '../../components/ui/Skeleton';
import { EmptyState } from '../../components/ui/EmptyState';
import { Spinner } from '../../components/ui/Spinner';
import { useAuth } from '../../contexts/AuthContext';
import { getNetworkErrorMessage, listApiKeys, revokeApiKey, createApplication } from '../../lib/api';
import type { ApiKeyRecord } from '../../lib/types';

export function ApiKeysPage() {
  const { user } = useAuth();
  const { addToast } = useToast();
  const { openModal } = useModal();

  const [keys, setKeys] = useState<ApiKeyRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Mask/Reveal state per key ID
  const [revealedKeys, setRevealedKeys] = useState<{ [id: string]: boolean }>({});

  // Create Key / App modal
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [keyAppName, setKeyAppName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newlyCreatedKey, setNewlyCreatedKey] = useState<{ prefix: string; rawKey: string; appName: string } | null>(null);

  const fetchKeys = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await listApiKeys();
      setKeys(data.apiKeys || []);
    } catch (err) {
      setError(getNetworkErrorMessage(err, 'Failed to load API keys.'));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user?.emailVerified) {
      fetchKeys();
    } else {
      setIsLoading(false);
    }
  }, [user?.emailVerified, fetchKeys]);

  const handleRevokeKey = (keyId: string, prefix: string) => {
    openModal({
      title: 'Revoke API Key?',
      description: `Key prefix "${prefix}..." will be permanently revoked. Clients using this key will immediately receive HTTP 401 Unauthorized.`,
      confirmText: 'Revoke Key',
      cancelText: 'Cancel',
      variant: 'destructive',
      onConfirm: async () => {
        try {
          await revokeApiKey(keyId);
          addToast({ type: 'success', message: `API key ${prefix}... revoked successfully.` });
          await fetchKeys();
        } catch (err) {
          addToast({
            type: 'error',
            message: getNetworkErrorMessage(err, 'Failed to revoke key.'),
          });
        }
      },
    });
  };

  const handleCreateKeySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!keyAppName.trim()) return;

    setIsSubmitting(true);
    try {
      const res = await createApplication(keyAppName.trim());
      setKeyAppName('');
      setIsCreateModalOpen(false);
      await fetchKeys();

      setNewlyCreatedKey({
        prefix: res.apiKey.substring(0, 14),
        rawKey: res.apiKey,
        appName: res.name,
      });

      addToast({ type: 'success', message: 'New API Key generated successfully!' });
    } catch (err) {
      addToast({
        type: 'error',
        message: getNetworkErrorMessage(err, 'Failed to generate API Key.'),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleRevealKey = (keyId: string) => {
    setRevealedKeys((prev) => ({ ...prev, [keyId]: !prev[keyId] }));
  };

  // Email verification guard banner
  if (!user?.emailVerified) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-ink">API Keys</h1>
          <p className="mt-1 text-sm text-sub">Manage and revoke your application API keys.</p>
        </div>

        <div className="rounded-2xl border border-warn/30 bg-warn/10 p-8 text-center space-y-4 max-w-2xl mx-auto shadow-card">
          <p className="text-lg font-bold text-warn flex items-center justify-center gap-2">
            <span>⚠️</span> Email Verification Required
          </p>
          <p className="text-xs text-sub leading-relaxed">
            API key management and secret generation require a verified email address. Please click the verification link sent to your email or request a new verification email in your account settings.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header & Create Key button */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-ink">API Keys</h1>
          <p className="mt-1 text-sm text-sub">
            Manage, reveal, and revoke API keys used to authenticate consent endpoints.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setIsCreateModalOpen(true)}
          className="btn-p text-sm font-semibold py-2.5 px-5 cursor-pointer shadow-md"
        >
          + Generate New Key
        </button>
      </div>

      {/* Security Info Card */}
      <div className="rounded-2xl border border-line bg-surface p-5 shadow-card space-y-2">
        <h3 className="text-xs font-bold uppercase tracking-wider text-muted flex items-center gap-1.5">
          🔑 Cryptographic Key Lifecycle
        </h3>
        <p className="text-xs text-sub leading-relaxed">
          API keys are hashed with HMAC-SHA256 before storage. We only display the prefix of active keys. To generate a new scoped key, register a client application.
        </p>
      </div>

      {/* Error state */}
      {error && (
        <div className="rounded-xl border border-err/30 bg-err/10 p-4 flex items-center justify-between text-sm text-err">
          <span>⚠️ {error}</span>
          <button type="button" onClick={fetchKeys} className="text-xs font-bold underline hover:no-underline ml-4">
            Retry
          </button>
        </div>
      )}

      {/* API Keys List */}
      {isLoading ? (
        <div className="space-y-4">
          <SkeletonCard />
          <SkeletonCard />
        </div>
      ) : keys.length === 0 ? (
        <EmptyState
          icon="🔑"
          title="No API Keys Found"
          description="API keys are generated when you register a new application."
          action={
            <button
              type="button"
              onClick={() => setIsCreateModalOpen(true)}
              className="btn-p text-xs py-2 px-4 cursor-pointer"
            >
              + Generate Key
            </button>
          }
        />
      ) : (
        <div className="space-y-4">
          {keys.map((key) => {
            const isRevealed = !!revealedKeys[key.id];
            return (
              <div
                key={key.id}
                className="rounded-2xl border border-line bg-surface p-5 shadow-card hover:shadow-cardhover transition-all flex flex-col sm:flex-row sm:items-center justify-between gap-4"
              >
                <div>
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-sm font-bold text-ink">
                      {isRevealed ? `${key.prefix}********************` : `${key.prefix}...`}
                    </span>
                    <span className="inline-flex rounded-full border border-ok/30 bg-ok/10 px-2 py-0.5 text-[9px] font-bold text-ok uppercase">
                      Active
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-sub">
                    Application: <strong className="text-ink">{key.app_name}</strong> · Created{' '}
                    {new Date(key.created_at * 1000).toLocaleDateString()}
                  </p>
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  <button
                    type="button"
                    onClick={() => toggleRevealKey(key.id)}
                    className="btn-g text-xs py-1.5 px-3"
                  >
                    {isRevealed ? 'Mask' : 'Show Prefix'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      navigator.clipboard.writeText(key.prefix);
                      addToast({ type: 'success', message: 'API key prefix copied!' });
                    }}
                    className="btn-g text-xs py-1.5 px-3"
                  >
                    Copy Prefix
                  </button>
                  <button
                    type="button"
                    onClick={() => handleRevokeKey(key.id, key.prefix)}
                    className="rounded-xl border border-err/30 bg-err/10 px-3 py-1.5 text-xs font-semibold text-err hover:bg-err/20 transition"
                  >
                    Revoke
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create Key Modal */}
      {isCreateModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 backdrop-blur-sm p-4 overflow-y-auto">
          <div className="w-full max-w-md rounded-2xl border border-line bg-card p-6 shadow-cardhover space-y-6">
            <div className="flex items-center justify-between border-b border-line pb-4">
              <h3 className="text-lg font-bold text-ink flex items-center gap-2">
                <span>🔑</span> Generate API Key
              </h3>
              <button
                type="button"
                onClick={() => setIsCreateModalOpen(false)}
                className="text-sub hover:text-ink text-sm font-bold p-1"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleCreateKeySubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-muted mb-1.5">
                  Application Scoped Name *
                </label>
                <input
                  type="text"
                  placeholder="e.g. Analytics Backend Key"
                  value={keyAppName}
                  onChange={(e) => setKeyAppName(e.target.value)}
                  className="w-full rounded-xl border border-line bg-surface px-4 py-2.5 text-sm text-ink placeholder-muted focus:border-blue-500 outline-none"
                  disabled={isSubmitting}
                  required
                />
              </div>

              <div className="pt-4 flex items-center justify-end gap-3 border-t border-line">
                <button
                  type="button"
                  onClick={() => setIsCreateModalOpen(false)}
                  className="btn-g text-xs py-2.5 px-4"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting || !keyAppName.trim()}
                  className="btn-p text-xs py-2.5 px-5 disabled:opacity-50 flex items-center gap-2"
                >
                  {isSubmitting ? <Spinner size="sm" /> : 'Generate Key'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Newly Created Secret Modal */}
      {newlyCreatedKey && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/70 backdrop-blur-sm p-4 overflow-y-auto">
          <div className="w-full max-w-lg rounded-2xl border border-line bg-card p-6 shadow-cardhover space-y-6">
            <div className="flex items-center gap-3">
              <span className="text-3xl">🎉</span>
              <div>
                <h3 className="text-lg font-bold text-ink">API Key Generated</h3>
                <p className="text-xs text-sub mt-0.5">Scoped for application "{newlyCreatedKey.appName}"</p>
              </div>
            </div>

            <div className="rounded-xl border border-warn/30 bg-warn/10 p-4 text-xs text-warn">
              <strong className="block font-bold mb-1">⚠️ Store Secret Securely</strong>
              This API key will only be displayed once. Copy it now and store it in your environment config.
            </div>

            <div>
              <label className="block text-[10px] font-bold uppercase tracking-wider text-muted mb-1">
                API Key Secret
              </label>
              <div className="flex items-center justify-between rounded-xl border border-line bg-surface p-3">
                <span className="font-mono text-xs text-ink truncate pr-3">{newlyCreatedKey.rawKey}</span>
                <button
                  type="button"
                  onClick={() => {
                    navigator.clipboard.writeText(newlyCreatedKey.rawKey);
                    addToast({ type: 'success', message: 'API Key copied to clipboard!' });
                  }}
                  className="text-xs font-bold text-blue-600 hover:text-blue-500"
                >
                  Copy Key
                </button>
              </div>
            </div>

            <div className="pt-4 border-t border-line flex justify-end">
              <button
                type="button"
                onClick={() => setNewlyCreatedKey(null)}
                className="btn-p text-xs py-2.5 px-5"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Export default alias as ApiKeysView for backward compat
export { ApiKeysPage as ApiKeysView };
